import { useEffect, useMemo, useRef, useState } from "react";
import type { SessionSummary } from "../types";

interface Action {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
}

interface Props {
  onClose: () => void;
  onOpenSession: (id: string) => void;
  actions: Action[];
}

export type { Action as CommandPaletteAction };

/** Paleta de comandos universal: busca ações do app + conversas, tudo num lugar só. */
export function CommandPalette({ onClose, onOpenSession, actions }: Props) {
  const [query, setQuery] = useState("");
  const [sessionResults, setSessionResults] = useState<SessionSummary[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setSessionResults([]);
      return;
    }
    let alive = true;
    const handle = setTimeout(() => {
      fetch(`/api/sessions?q=${encodeURIComponent(q)}&limit=6`)
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .then((j: { items: SessionSummary[] }) => {
          if (alive) setSessionResults(j.items ?? []);
        })
        .catch(() => {
          if (alive) setSessionResults([]);
        });
    }, 180);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [query]);

  const filteredActions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((a) => a.label.toLowerCase().includes(q));
  }, [actions, query]);

  type Entry = { key: string; label: string; hint?: string; run: () => void };
  const entries: Entry[] = useMemo(() => {
    const sessionEntries: Entry[] = sessionResults.map((s) => ({
      key: `session-${s.id}`,
      label: s.title,
      hint: s.matchSnippet ?? "conversa",
      run: () => onOpenSession(s.id),
    }));
    const actionEntries: Entry[] = filteredActions.map((a) => ({ key: `action-${a.id}`, label: a.label, hint: a.hint, run: a.run }));
    return [...actionEntries, ...sessionEntries];
  }, [filteredActions, sessionResults, onOpenSession]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const execute = (entry: Entry | undefined) => {
    if (!entry) return;
    entry.run();
    onClose();
  };

  return (
    <div className="modal-overlay command-palette-overlay" onClick={onClose} role="presentation">
      <div
        className="command-palette"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Paleta de comandos"
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((i) => Math.min(i + 1, entries.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            execute(entries[selectedIndex]);
          } else if (e.key === "Escape") {
            onClose();
          }
        }}
      >
        <input
          ref={inputRef}
          className="command-palette-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Digite um comando ou busque uma conversa…"
          spellCheck={false}
        />
        <div className="command-palette-list">
          {entries.length === 0 ? (
            <p className="prompt-list-status">Nada encontrado.</p>
          ) : (
            entries.map((entry, i) => (
              <button
                key={entry.key}
                type="button"
                className={`command-palette-item ${i === selectedIndex ? "active" : ""}`}
                onMouseEnter={() => setSelectedIndex(i)}
                onClick={() => execute(entry)}
              >
                <span className="command-palette-item-label">{entry.label}</span>
                {entry.hint && <span className="command-palette-item-hint">{entry.hint}</span>}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
